import { useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight, ArrowLeft, Loader2, AlertCircle, Plus, Minus, Edit3, FileText, Lightbulb, Network, Hash } from 'lucide-react';
import { motion } from 'motion/react';
import type { BundleConcept, BundleSource, BundleSummary, Capsule, CapsuleBundle, KGTriple } from '../types';
import type { CollectClient } from '../lib/api';
import { ApiError } from '../lib/api';
import { capsuleErrorMessage } from '../lib/capsuleErrors';

/**
 * CapsuleCompare — fetches two capsule bundles, runs a diff over sources /
 * summaries / concepts / KG triples / purpose, and renders the changes.
 *
 * Diff semantics:
 *   - sources / summaries / concepts: compared by `path`. In A only = removed,
 *     in B only = added, in both with different `content` = modified.
 *   - kg_triples: compared by `${subject}|${predicate}|${object}` key.
 *     Set semantics: in A only = removed, in B only = added.
 *   - purpose: free-text, shown side-by-side if different, single block if same.
 *
 * Both bundles are decompressed + SHA-256-verified by the same getCapsulePayload
 * the contents viewer uses, so consistency is guaranteed at the bytes level.
 */
export function CapsuleCompare({
  client,
  capsuleA,
  capsuleB,
  onBack,
}: {
  client: CollectClient;
  capsuleA: Capsule;
  capsuleB: Capsule;
  onBack: () => void;
}) {
  const [bundleA, setBundleA] = useState<CapsuleBundle | null>(null);
  const [bundleB, setBundleB] = useState<CapsuleBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBundleA(null);
    setBundleB(null);
    Promise.all([
      client.getCapsulePayload(capsuleA.id),
      client.getCapsulePayload(capsuleB.id),
    ])
      .then(([a, b]) => {
        if (cancelled) return;
        setBundleA(a);
        setBundleB(b);
      })
      .catch((e: ApiError) => {
        if (cancelled) return;
        setError(capsuleErrorMessage(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [client, capsuleA.id, capsuleB.id]);

  const diff = useMemo(() => {
    if (!bundleA || !bundleB) return null;
    return computeDiff(bundleA, bundleB);
  }, [bundleA, bundleB]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <button onClick={onBack} className="btn btn-ghost mb-6">
        <ArrowLeft size={16} />
        Back to capsule
      </button>

      {/* Hero — A → B */}
      <div className="glass-panel-strong rounded-2xl p-8 mb-6">
        <div className="flex items-center gap-4 mb-2">
          <ArrowLeftRight size={24} style={{ color: '#10B981' }} className="heartbeat" />
          <h1 className="display text-3xl">Compare capsules</h1>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <CapsuleSummary label="A — older" capsule={capsuleA} />
          <CapsuleSummary label="B — newer" capsule={capsuleB} />
        </div>
      </div>

      {loading && (
        <div className="glass-panel rounded-xl p-12 flex items-center gap-3 justify-center" style={{ color: 'rgba(240,253,244,0.55)' }}>
          <Loader2 className="animate-spin" size={18} />
          <span className="text-sm">Decompressing both bundles…</span>
        </div>
      )}

      {error && (
        <div className="glass-panel rounded-xl p-6" style={{ borderColor: 'rgba(239,68,68,0.3)' }}>
          <div className="flex items-start gap-3">
            <AlertCircle size={20} style={{ color: '#F87171' }} className="flex-none mt-0.5" />
            <div className="text-sm" style={{ color: '#FCA5A5' }}>{error}</div>
          </div>
        </div>
      )}

      {diff && (
        <>
          <DiffSummary diff={diff} />
          <DiffSection
            icon={<FileText size={14} />}
            label="Sources"
            added={diff.sources.added.map(fileItem)}
            removed={diff.sources.removed.map(fileItem)}
            modified={diff.sources.modified.map(modItem)}
          />
          {(diff.summaries.added.length || diff.summaries.removed.length || diff.summaries.modified.length) > 0 && (
            <DiffSection
              icon={<FileText size={14} />}
              label="Summaries"
              added={diff.summaries.added.map(fileItem)}
              removed={diff.summaries.removed.map(fileItem)}
              modified={diff.summaries.modified.map(modItem)}
            />
          )}
          <DiffSection
            icon={<Lightbulb size={14} />}
            label="Concepts"
            added={diff.concepts.added.map(fileItem)}
            removed={diff.concepts.removed.map(fileItem)}
            modified={diff.concepts.modified.map(modItem)}
          />
          <DiffSection
            icon={<Network size={14} />}
            label="Knowledge graph triples"
            added={diff.triples.added.map(tripleItem)}
            removed={diff.triples.removed.map(tripleItem)}
            modified={[]}
          />
          {(bundleA?.purpose || bundleB?.purpose) && (
            <PurposeDiff a={bundleA?.purpose || ''} b={bundleB?.purpose || ''} />
          )}
        </>
      )}
    </motion.div>
  );
}

// ─── UI helpers ─────────────────────────────────────────────────────────────

function CapsuleSummary({ label, capsule }: { label: string; capsule: Capsule }) {
  return (
    <div className="glass-panel rounded-xl p-4">
      <div className="label mb-2">{label}</div>
      <div className="font-mono text-sm font-semibold mb-1 truncate" title={capsule.name}>{capsule.name}</div>
      <div className="text-xs" style={{ color: 'rgba(240,253,244,0.5)' }}>
        {formatBytes(capsule.content_size)} · {capsule.create_date} · v{capsule.version}
      </div>
      <div className="hash mt-1 truncate">{capsule.content_hash.slice(0, 24)}…</div>
    </div>
  );
}

function DiffSummary({ diff }: { diff: BundleDiff }) {
  const totalAdded =
    diff.sources.added.length + diff.summaries.added.length +
    diff.concepts.added.length + diff.triples.added.length;
  const totalRemoved =
    diff.sources.removed.length + diff.summaries.removed.length +
    diff.concepts.removed.length + diff.triples.removed.length;
  const totalModified =
    diff.sources.modified.length + diff.summaries.modified.length +
    diff.concepts.modified.length;

  if (totalAdded === 0 && totalRemoved === 0 && totalModified === 0) {
    return (
      <div className="glass-panel rounded-xl p-6 mb-4 text-center">
        <span className="text-sm" style={{ color: 'rgba(240,253,244,0.6)' }}>
          No content differences. Both bundles describe the same wiki state — only metadata varies (name, hash, timestamp).
        </span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3 mb-4">
      <SumCard label="Added" count={totalAdded} icon={<Plus size={14} />} color="#10B981" />
      <SumCard label="Removed" count={totalRemoved} icon={<Minus size={14} />} color="#F87171" />
      <SumCard label="Modified" count={totalModified} icon={<Edit3 size={14} />} color="#FBBF24" />
    </div>
  );
}

function SumCard({ label, count, icon, color }: { label: string; count: number; icon: React.ReactNode; color: string }) {
  return (
    <div className="glass-panel rounded-xl p-4 flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-none" style={{
        background: `${color}22`, color,
      }}>{icon}</div>
      <div>
        <div className="font-mono text-2xl" style={{ color }}>{count}</div>
        <div className="label" style={{ fontSize: 9 }}>{label}</div>
      </div>
    </div>
  );
}

function DiffSection({
  icon, label, added, removed, modified,
}: {
  icon: React.ReactNode;
  label: string;
  added: DiffRow[];
  removed: DiffRow[];
  modified: DiffRow[];
}) {
  if (added.length === 0 && removed.length === 0 && modified.length === 0) {
    return null; // hide whole section if no changes
  }
  return (
    <div className="glass-panel rounded-xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-3" style={{ color: '#10B981' }}>
        {icon}
        <h3 className="font-semibold">{label}</h3>
        <span className="label ml-auto">
          {added.length > 0 && <span style={{ color: '#34D399' }}>+{added.length} </span>}
          {removed.length > 0 && <span style={{ color: '#F87171' }}>-{removed.length} </span>}
          {modified.length > 0 && <span style={{ color: '#FBBF24' }}>~{modified.length}</span>}
        </span>
      </div>
      <ul className="space-y-1.5">
        {added.map((r, i) => <DiffItem key={`a-${i}`} kind="added" {...r} />)}
        {removed.map((r, i) => <DiffItem key={`r-${i}`} kind="removed" {...r} />)}
        {modified.map((r, i) => <DiffItem key={`m-${i}`} kind="modified" {...r} />)}
      </ul>
    </div>
  );
}

function DiffItem({ kind, primary, secondary }: { kind: 'added' | 'removed' | 'modified'; primary: string; secondary?: string }) {
  const styles = {
    added: { bg: 'rgba(16,185,129,0.08)', color: '#34D399', symbol: '+' },
    removed: { bg: 'rgba(239,68,68,0.08)', color: '#F87171', symbol: '−' },
    modified: { bg: 'rgba(251,191,36,0.08)', color: '#FBBF24', symbol: '~' },
  }[kind];
  return (
    <li className="flex items-start gap-2 px-3 py-1.5 rounded text-xs" style={{ background: styles.bg }}>
      <span className="font-mono font-bold flex-none w-3 text-center" style={{ color: styles.color }}>{styles.symbol}</span>
      <div className="flex-1 min-w-0">
        <div className="font-mono break-all" style={{ color: '#F0FDF4' }}>{primary}</div>
        {secondary && <div className="font-mono" style={{ color: 'rgba(240,253,244,0.5)', fontSize: 11 }}>{secondary}</div>}
      </div>
    </li>
  );
}

function PurposeDiff({ a, b }: { a: string; b: string }) {
  if (a === b) {
    return null; // unchanged purpose, hide
  }
  return (
    <div className="glass-panel rounded-xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-3" style={{ color: '#FBBF24' }}>
        <Hash size={14} />
        <h3 className="font-semibold">Purpose changed</h3>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <div className="label mb-1" style={{ color: '#F87171' }}>− A</div>
          <pre className="text-xs whitespace-pre-wrap font-sans p-3 rounded" style={{ background: 'rgba(239,68,68,0.05)', color: 'rgba(240,253,244,0.85)', maxHeight: 240, overflow: 'auto' }}>{a || '(empty)'}</pre>
        </div>
        <div>
          <div className="label mb-1" style={{ color: '#34D399' }}>+ B</div>
          <pre className="text-xs whitespace-pre-wrap font-sans p-3 rounded" style={{ background: 'rgba(16,185,129,0.05)', color: 'rgba(240,253,244,0.85)', maxHeight: 240, overflow: 'auto' }}>{b || '(empty)'}</pre>
        </div>
      </div>
    </div>
  );
}

// ─── Diff core ──────────────────────────────────────────────────────────────

interface FileDiff<T> {
  added: T[];
  removed: T[];
  modified: { path: string; sizeA: number; sizeB: number; deltaBytes: number }[];
  unchanged: T[];
}

interface TripleDiff {
  added: KGTriple[];
  removed: KGTriple[];
  unchanged: KGTriple[];
}

interface BundleDiff {
  sources: FileDiff<BundleSource>;
  summaries: FileDiff<BundleSummary>;
  concepts: FileDiff<BundleConcept>;
  triples: TripleDiff;
}

function diffByPath<T extends { path: string; size: number; content: string }>(a: T[], b: T[]): FileDiff<T> {
  const mapA = new Map(a.map((x) => [x.path, x]));
  const mapB = new Map(b.map((x) => [x.path, x]));
  const added: T[] = [];
  const removed: T[] = [];
  const modified: { path: string; sizeA: number; sizeB: number; deltaBytes: number }[] = [];
  const unchanged: T[] = [];

  for (const [path, x] of mapB) {
    const inA = mapA.get(path);
    if (!inA) {
      added.push(x);
    } else if (inA.content !== x.content) {
      modified.push({ path, sizeA: inA.size, sizeB: x.size, deltaBytes: x.size - inA.size });
    } else {
      unchanged.push(x);
    }
  }
  for (const [path, x] of mapA) {
    if (!mapB.has(path)) removed.push(x);
  }
  return { added, removed, modified, unchanged };
}

function diffTriples(a: KGTriple[], b: KGTriple[]): TripleDiff {
  const key = (t: KGTriple) => `${t.subject} ${t.predicate} ${t.object}`;
  const setA = new Map(a.map((t) => [key(t), t]));
  const setB = new Map(b.map((t) => [key(t), t]));
  const added: KGTriple[] = [];
  const removed: KGTriple[] = [];
  const unchanged: KGTriple[] = [];
  for (const [k, t] of setB) {
    if (setA.has(k)) unchanged.push(t);
    else added.push(t);
  }
  for (const [k, t] of setA) {
    if (!setB.has(k)) removed.push(t);
  }
  return { added, removed, unchanged };
}

function computeDiff(a: CapsuleBundle, b: CapsuleBundle): BundleDiff {
  return {
    sources: diffByPath(a.sources || [], b.sources || []),
    summaries: diffByPath(a.summaries || [], b.summaries || []),
    concepts: diffByPath(a.concepts || [], b.concepts || []),
    triples: diffTriples(a.kg_triples || [], b.kg_triples || []),
  };
}

// ─── Row renderers (return DiffRow shape) ──────────────────────────────────

interface DiffRow {
  primary: string;
  secondary?: string;
}

function fileItem(f: { path: string; size: number }): DiffRow {
  return { primary: f.path, secondary: formatBytes(f.size) };
}

function modItem(m: { path: string; sizeA: number; sizeB: number; deltaBytes: number }): DiffRow {
  const delta = m.deltaBytes > 0 ? `+${formatBytes(m.deltaBytes)}` : `-${formatBytes(-m.deltaBytes)}`;
  return { primary: m.path, secondary: `${formatBytes(m.sizeA)} → ${formatBytes(m.sizeB)} (${delta})` };
}

function tripleItem(t: KGTriple): DiffRow {
  return { primary: `${t.subject} —${t.predicate}→ ${t.object}` };
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
